import CoursesService from "services/CoursesService";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

export const getCourses = createAsyncThunk(
  "courses/getAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await CoursesService.getCourses();
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Erreur");
    }
  }
);

export const getCourseById = createAsyncThunk(
  "courses/getCourseById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await CoursesService.getCourse(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Erreur");
    }
  }
);

export const createCourse = createAsyncThunk(
  "courses/createCourse",
  async (data, { rejectWithValue }) => {
    try {
      const response = await CoursesService.createCourse(data);
      return response.data.course;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Erreur");
    }
  }
);

export const updateCourseById = createAsyncThunk(
  "courses/updateCourseById",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await CoursesService.updateCourse(id, data);
      return response.data.course;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Erreur");
    }
  }
);

export const updateCourseStatus = createAsyncThunk(
  "courses/updateCourseStatus",
  async ({ id, state }, { rejectWithValue }) => {
    try {
      const response = await CoursesService.updateCourseStatus(id, state);
      return response.data.course;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Erreur");
    }
  }
);

export const deleteCourseById = createAsyncThunk(
  "courses/deleteCourseById",
  async (id, { rejectWithValue }) => {
    try {
      await CoursesService.deleteCourse(id);
      return id; // Retourner l'ID pour pouvoir filtrer le cours dans le reducer
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Erreur");
    }
  }
);

export const setCourseFormations = createAsyncThunk(
  "courses/setCourseFormations",
  async ({ id, formationIds }, { rejectWithValue }) => {
    try {
      const response = await CoursesService.setCourseFormations(
        id,
        formationIds
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Erreur");
    }
  }
);

const initialState = {
  courses: [],
  course: null,
  loading: false,
  error: null,
};

const coursesSlice = createSlice({
  name: "courses",
  initialState,
  reducers: {
    setCourses: (state, { payload }) => {
      state.courses = payload;
    },
    setCourse: (state, { payload }) => {
      state.course = payload;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setError: (state, { payload }) => {
      state.error = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET ALL COURSES
      .addCase(getCourses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCourses.fulfilled, (state, { payload }) => {
        state.courses = payload;
        state.loading = false;
      })
      .addCase(getCourses.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })

      // GET ONE COURSE
      .addCase(getCourseById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCourseById.fulfilled, (state, { payload }) => {
        state.course = payload;
        state.loading = false;
      })
      .addCase(getCourseById.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })

      // CREATE COURSE
      .addCase(createCourse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCourse.fulfilled, (state, { payload }) => {
        state.courses.push(payload);
        state.loading = false;
      })
      .addCase(createCourse.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })

      // UPDATE COURSE
      .addCase(updateCourseById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCourseById.fulfilled, (state, { payload }) => {
        const index = state.courses.findIndex(
          (course) => course._id === payload._id
        );
        if (index !== -1) {
          state.courses[index] = payload;
        }
        state.course = payload;
        state.loading = false;
      })
      .addCase(updateCourseById.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })

      // UPDATE COURSE STATUS
      .addCase(updateCourseStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCourseStatus.fulfilled, (state, { payload }) => {
        const index = state.courses.findIndex(
          (course) => course._id === payload._id
        );
        if (index !== -1) {
          state.courses[index] = payload;
        }
        if (state.course && state.course._id === payload._id) {
          state.course = payload;
        }
        state.loading = false;
      })
      .addCase(updateCourseStatus.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })

      // DELETE COURSE
      .addCase(deleteCourseById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCourseById.fulfilled, (state, { payload }) => {
        state.courses = state.courses.filter(
          (course) => course._id !== payload
        );
        if (state.course && state.course._id === payload) {
          state.course = null;
        }
        state.loading = false;
      })
      .addCase(deleteCourseById.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      });
  },
});

export const { setCourses, setCourse, setLoading, setError } =
  coursesSlice.actions;

export default coursesSlice.reducer;
